import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { getCurrentMembershipYear, getAvailableMembershipYears } from '../../utils/membershipYear';

const COLORS = ['#006e39', '#0077b6', '#e76f51', '#2a9d8f', '#f4a261', '#9c27b0', '#ff9800', '#607d8b'];

const AdminMembershipDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  // Dashboard data
  const [summary, setSummary] = useState(null);
  const [membersByRegion, setMembersByRegion] = useState([]);
  const [membersByTerritory, setMembersByTerritory] = useState([]);
  const [membersBySda, setMembersBySda] = useState([]);
  const [membersByDiscipline, setMembersByDiscipline] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [trends, setTrends] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedYear, setSelectedYear] = useState(getCurrentMembershipYear().toString());

  // Elenco dinamico degli anni sociali disponibili per la dropdown
  const availableYears = useMemo(() => getAvailableMembershipYears(), []);

  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const params = new URLSearchParams();
      if (selectedYear) {
        params.append('year', selectedYear);
      }
      const qs = params.toString() ? `?${params.toString()}` : '';

      // Fetch summary
      const summaryRes = await api.get(`/admin/dashboard/membership-summary${qs}`);
      setSummary(summaryRes.data.data);

      // Fetch members by region
      const regionRes = await api.get(`/admin/dashboard/membership-by-region${qs}`);
      setMembersByRegion(regionRes.data.data.regions);

      // Fetch members by territory
      const territoryParams = new URLSearchParams(params);
      if (selectedRegion) {
        territoryParams.append('region', selectedRegion);
      }
      const territoryUrl = `/admin/dashboard/membership-by-territory${territoryParams.toString() ? `?${territoryParams.toString()}` : ''}`;
      const territoryRes = await api.get(territoryUrl);
      setMembersByTerritory(territoryRes.data.data.territories);

      // Fetch members by SDA (macro-categoria)
      const sdaRes = await api.get(`/admin/dashboard/membership-by-sda${qs}`);
      setMembersBySda(sdaRes.data.data.sdas);

      // Fetch members by discipline
      const disciplineRes = await api.get(`/admin/dashboard/membership-by-discipline${qs}`);
      setMembersByDiscipline(disciplineRes.data.data.disciplines);

      // Fetch top associations
      const limit = 10;
      const assocParams = new URLSearchParams(params);
      assocParams.append('limit', limit);
      const assocRes = await api.get(`/admin/dashboard/top-associations?${assocParams.toString()}`);
      setAssociations(assocRes.data.data.associations);

      // Fetch trends (last 12 months)
      const trendsParams = new URLSearchParams(params);
      trendsParams.append('months', '12');
      const trendsRes = await api.get(`/admin/dashboard/membership-trends?${trendsParams.toString()}`);
      setTrends(trendsRes.data.data.trends);

      setLastUpdate(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching admin membership dashboard data:', err);
      setError(err.response?.data?.message || 'Errore nel caricamento dei dati della dashboard');
      setLoading(false);
    }
  }, [selectedRegion, selectedYear, api]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Handle region filter change
  const handleRegionChange = (e) => {
    setSelectedRegion(e.target.value);
  };

  // Handle year filter change
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // Format month for charts
  const formatMonth = (month) => {
    if (!month) return '';
    const [year, m] = month.split('-');
    const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return `${months[parseInt(m) - 1]} ${year.slice(2)}`;
  };

  // Prepare gender data for pie chart
  const getGenderData = () => {
    if (!summary?.genderDistribution) return [];
    return Object.entries(summary.genderDistribution).map(([name, value]) => ({
      name,
      value
    }));
  };

  // Prepare age data for bar chart
  const getAgeData = () => {
    if (!summary?.ageDistribution) return [];
    return summary.ageDistribution;
  };

  if (loading && !summary) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status" style={{ color: '#006e39' }}>
          <span className="visually-hidden">Caricamento...</span>
        </Spinner>
        <p className="mt-3">Caricamento dashboard tesserati...</p>
      </Container>
    );
  }

  if (error && !summary) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Errore</Alert.Heading>
          <p>{error}</p>
          <Button onClick={fetchDashboardData} variant="outline-danger">Riprova</Button>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="container-fluid min-vh-100" style={{ background: 'linear-gradient(135deg, #006e39, #52b788)' }}>
      <div className="row p-4">
        <div className="col-12">
          <div className="glass-container p-4 rounded-4" style={{ backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            
            {/* Header */}
            <Row className="mb-4">
              <Col>
                <h1 style={{ color: "#006e39" }} className="mb-2">
                  <i className="bi bi-graph-up me-3"></i>
                  Dashboard Tesserati Admin
                </h1>
                <p className="text-muted mb-0">
                  <i className="bi bi-person-badge me-2"></i>
                  {user?.email} - <strong>{user?.role}</strong>
                  {['sda_regional', 'sda_territorial'].includes(user?.role) && user?.discipline && (
                    <Badge bg="success" className="ms-2">
                      <i className="bi bi-funnel me-1"></i>
                      Disciplina: {user.discipline}
                    </Badge>
                  )}
                </p>
                <small className="text-muted">
                  Ultimo aggiornamento: {lastUpdate.toLocaleString('it-IT')}
                </small>
              </Col>
              <Col xs="auto" className="d-flex gap-2">
                <Form.Select
                  size="md"
                  style={{ width: '150px' }}
                  value={selectedYear}
                  onChange={handleYearChange}
                >
                  <option value="">Tutti gli anni</option>
                  {availableYears.map(y => (
                    <option key={y} value={y.toString()}>{y}</option>
                  ))}
                </Form.Select>
                <Button
                  variant="outline-success"
                  onClick={fetchDashboardData}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-arrow-clockwise me-2"></i>Aggiorna</>}
                </Button>
              </Col>
            </Row>

            {/* Summary Cards */}
            {summary && (
              <Row className="mb-4">
                <Col md={4}>
                  <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #006e39 !important' }}>
                    <Card.Body className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#006e3915' }}>
                        <i className="bi bi-people-fill fs-2" style={{ color: '#006e39' }}></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-1">Tesserati Totali</h6>
                        <h2 className="mb-0" style={{ color: '#006e39' }}>{summary.overview.totalMembers.toLocaleString('it-IT')}</h2>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #0077b6 !important' }}>
                    <Card.Body className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#0077b615' }}>
                        <i className="bi bi-building fs-2" style={{ color: '#0077b6' }}></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-1">Associazioni</h6>
                        <h2 className="mb-0" style={{ color: '#0077b6' }}>{summary.overview.totalAssociations.toLocaleString('it-IT')}</h2>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: '4px solid #2a9d8f !important' }}>
                    <Card.Body className="d-flex align-items-center">
                      <div className="rounded-circle p-3 me-3" style={{ backgroundColor: '#2a9d8f15' }}>
                        <i className="bi bi-person-plus-fill fs-2" style={{ color: '#2a9d8f' }}></i>
                      </div>
                      <div>
                        <h6 className="text-muted mb-1">Nuovi (30gg)</h6>
                        <h2 className="mb-0" style={{ color: '#2a9d8f' }}>{summary.overview.newMembers30d.toLocaleString('it-IT')}</h2>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Charts Row 1: Gender & Age Distribution */}
            <Row className="mb-4">
              <Col lg={4}>
                <Card className="h-100">
                  <Card.Header className="bg-white">
                    <strong><i className="bi bi-gender-ambiguous me-2"></i>Distribuzione per Sesso</strong>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={getGenderData()}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {getGenderData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => value.toLocaleString('it-IT')} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={8}>
                <Card className="h-100">
                  <Card.Header className="bg-white">
                    <strong><i className="bi bi-bar-chart-fill me-2"></i>Distribuzione per Età</strong>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={getAgeData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="ageGroup" />
                        <YAxis />
                        <Tooltip formatter={(value) => value.toLocaleString('it-IT')} />
                        <Bar dataKey="count" fill="#006e39" name="Tesserati" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Charts Row 2: SDA & Discipline Distribution */}
            <Row className="mb-4">
              <Col lg={6}>
                <Card>
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <strong><i className="bi bi-award me-2"></i>Tesserati per SDA (Macro-categoria)</strong>
                    <Badge bg="info">{membersBySda.length} SDA</Badge>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={membersBySda} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="sda" type="category" width={150} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value) => value.toLocaleString('it-IT')} />
                        <Legend />
                        <Bar dataKey="memberCount" fill="#006e39" name="Tesserati" />
                        <Bar dataKey="associationCount" fill="#0077b6" name="Associazioni" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                <Card>
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <strong><i className="bi bi-trophy me-2"></i>Top 15 Discipline Sportive</strong>
                    <Badge bg="success">{membersByDiscipline.length} discipline</Badge>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={membersByDiscipline.slice(0, 15)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="discipline" type="category" width={150} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value) => value.toLocaleString('it-IT')} />
                        <Legend />
                        <Bar dataKey="memberCount" fill="#9c27b0" name="Tesserati" />
                        <Bar dataKey="associationCount" fill="#f4a261" name="Associazioni" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Charts Row 3: Regional & Territorial Distribution */}
            <Row className="mb-4">
              <Col lg={12}>
                <Card>
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <strong><i className="bi bi-map me-2"></i>Tesserati per Regione</strong>
                    <Badge bg="success">{membersByRegion.length} regioni</Badge>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={membersByRegion.slice(0, 15)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="region" type="category" width={150} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(value) => value.toLocaleString('it-IT')} />
                        <Legend />
                        <Bar dataKey="memberCount" fill="#006e39" name="Tesserati" />
                        <Bar dataKey="associationCount" fill="#0077b6" name="Associazioni" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Charts Row 4: Territory Distribution with Filter */}
            <Row className="mb-4">
              <Col lg={12}>
                <Card>
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <strong><i className="bi bi-geo-alt me-2"></i>Tesserati per Territorio</strong>
                    <Form.Select 
                      size="sm" 
                      style={{ width: '200px' }}
                      value={selectedRegion}
                      onChange={handleRegionChange}
                    >
                      <option value="">Tutte le regioni</option>
                      {membersByRegion.map(r => (
                        <option key={r.region} value={r.region}>{r.region}</option>
                      ))}
                    </Form.Select>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={membersByTerritory.slice(0, 15)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="territory" type="category" width={180} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(value) => value.toLocaleString('it-IT')} />
                        <Legend />
                        <Bar dataKey="memberCount" fill="#2a9d8f" name="Tesserati" />
                        <Bar dataKey="associationCount" fill="#e76f51" name="Associazioni" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Charts Row 5: Trends & Top Associations */}
            <Row className="mb-4">
              <Col lg={6}>
                <Card className="h-100">
                  <Card.Header className="bg-white">
                    <strong><i className="bi bi-graph-up-arrow me-2"></i>Trend Tesseramenti (12 mesi)</strong>
                  </Card.Header>
                  <Card.Body>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={trends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip 
                          labelFormatter={formatMonth}
                          formatter={(value) => value.toLocaleString('it-IT')} 
                        />
                        <Legend />
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="newMembers" 
                          stroke="#006e39" 
                          strokeWidth={2}
                          name="Nuovi Tesserati"
                          dot={{ r: 4 }}
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="cumulativeMembers" 
                          stroke="#0077b6" 
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          name="Cumulativo"
                          dot={{ r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="h-100">
                  <Card.Header className="bg-white">
                    <strong><i className="bi bi-trophy me-2"></i>Top 10 Associazioni</strong>
                  </Card.Header>
                  <Card.Body style={{ maxHeight: '350px', overflowY: 'auto' }}>
                    {associations.length > 0 ? (
                      <Table striped hover size="sm">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Associazione</th>
                            <th>Territorio</th>
                            <th>Tesserati</th>
                          </tr>
                        </thead>
                        <tbody>
                          {associations.map((assoc, idx) => (
                            <tr key={assoc.id}>
                              <td>
                                {idx < 3 ? (
                                  <Badge bg={idx === 0 ? 'warning' : idx === 1 ? 'secondary' : 'danger'}>
                                    {idx + 1}
                                  </Badge>
                                ) : (
                                  idx + 1
                                )}
                              </td>
                              <td>
                                <div className="fw-bold small">{assoc.name}</div>
                                <small className="text-muted">{assoc.associationCode}</small>
                              </td>
                              <td className="small">
                                {assoc.city && <span>{assoc.city}</span>}
                                {assoc.territory && <div className="text-muted">{assoc.territory}</div>}
                              </td>
                              <td>
                                <Badge bg="success">{assoc.memberCount}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">
                        Nessuna associazione trovata
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Info Alert */}
            <Alert variant="info" className="d-flex align-items-center">
              <i className="bi bi-info-circle me-3 fs-4"></i>
              <div>
                <strong>Dashboard Admin - Statistiche Tesserati</strong>
                <p className="mb-0 small">
                  Questa dashboard mostra le statistiche complete dei tesserati su tutte le discipline sportive.
                  Utilizza i filtri per visualizzare dati specifici per regione o territorio.
                </p>
              </div>
            </Alert>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMembershipDashboard;
